import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft,
  faChevronRight,
  faBook
} from '@fortawesome/free-solid-svg-icons';

// Contexts
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { BundleContext } from "../../context/BundleProvider";
// Components
import CommonHeader from "../../components/common/CommonHeader";
import NavBar from "../../components/NavBar";
import ArticleContent from "../../components/common/ArticleContent";
import UpgradeBanner from "../../components/common/UpgradeBanner";

const Article = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const set_id = state?.set_id ?? "";
  const lastIndex = state?.lastIndex || 0;
  const articleSet = state?.articles || [];
  const topic_name = state?.topic_name ?? "Articles";
  const { selectedCertificate } = useContext(BundleContext);
  
  
  const {
    profile,
    addArticleProgress,
    isSubscribe,
    articlesList,
  } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);

  const [articles, setArticles] = useState(articleSet);
  const [currentIndex, setCurrentIndex] = useState(lastIndex);
  const lastSavedIndexRef = useRef(null);
  const lastSavedArticleIdRef = useRef(null);

  const currentArticle = articles[currentIndex];

  // Handle navigation
  const handleNext = () => {
    if (currentIndex < articles.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
    }
  };

  // Save reading progress (non-blocking)
  const saveProgress = useCallback((index, articleId) => {
    if (!articleId || !set_id) return;

    // Prevent duplicate saves
    if (lastSavedIndexRef.current === index && lastSavedArticleIdRef.current === articleId) {
      return;
    }

    // Update refs immediately to prevent duplicate calls
    lastSavedIndexRef.current = index;
    lastSavedArticleIdRef.current = articleId;

    addArticleProgress(set_id, articleId);
  }, [set_id, addArticleProgress]);

  // Save progress when article index or article ID changes
  useEffect(() => {
    let isMounted = true;
    
    if (currentArticle?.id && currentIndex !== null && isMounted) {
      saveProgress(currentIndex, currentArticle.id);
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentIndex, currentArticle?.id, saveProgress]);

  if (!currentArticle) {
    return (
      <div className="min-h-screen transition-colors duration-300" 
           style={{ background: theme?.background?.main }}>
        <CommonHeader
          title="Article Not Found"
          icon={faBook}
          onBack={() => navigate(-1)}
          theme={theme}
          showBackButton={true}
        />
        <div className="text-center mt-8">
          <p style={{ color: theme?.text?.secondary }}>
            The requested article could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300"
         style={{ background: theme?.background?.main }}>
      <div style={{ paddingBottom: articles.length > 1 ? '7rem' : '0' }}>
        {/* Header */}
        <CommonHeader
          title={topic_name}
          subtitle={`Article ${currentIndex + 1} of ${articles.length}`}
          icon={faBook}
          onBack={() => navigate(-1)}
          theme={theme}
          showBackButton={true}
        />

        {/* Upgrade Banner for Free Users on Last Article */}
        {!isSubscribe && currentIndex === articles.length - 1 && articles.length > 0 && (
          <UpgradeBanner
            type="article"
            length={articlesList?.length}
            theme={theme}
            onUpgrade={() => navigate('/chooseplan')}
          />
        )}

        {/* Article Content */}
        <ArticleContent 
          article={currentArticle}
          showHeader={true}
          showBookmark={true}
          showStats={true}
        />
      </div>

      {/* Navigation Bar */}
      {articles.length > 1 && (
        <NavBar
          type="test"
          onNext={handleNext}
          onPrevious={handlePrevious}
          currentIndex={currentIndex}
          dataSet={articles.length}
          navTitle="Article"
          showEnd={false}
          onEndTest={() => {}}
        />
      )}
    </div>
  );
};

export default Article; 